import { useMemo } from 'react';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { Avatar, Dropdown, Layout, Menu, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  AppstoreOutlined,
  FileTextOutlined,
  LogoutOutlined,
  SafetyOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';

import { isHiddenCommunity, listCommunities } from '@/api/students';
import { useAuthStore } from '@/stores/authStore';
import type { CommunityOption } from '@/types/students';
import './app-layout.css';

const { Sider, Header, Content } = Layout;
const { Text } = Typography;

interface MenuGroup {
  parentKey: string;
  allKey: string;
  basePath: string;
  label: string;
  icon: React.ReactNode;
}

const STUDENTS_GROUP: MenuGroup = {
  parentKey: 'students',
  allKey: 'students-all',
  basePath: '/students',
  label: 'Học viên',
  icon: <TeamOutlined />,
};

const ASSIGNMENTS_GROUP: MenuGroup = {
  parentKey: 'assignments',
  allKey: 'assignments-all',
  basePath: '/assignments',
  label: 'Bài tập',
  icon: <FileTextOutlined />,
};

const MENU_GROUPS: readonly MenuGroup[] = [STUDENTS_GROUP, ASSIGNMENTS_GROUP];

// ---- Kỉ Luật (3-level nested) ---------------------------------------------
// Root group with one sub-section ("Bài tập") whose leaves are per-community
// menu items. Future Buổi Zoom sub-section will be added alongside Bài tập.
const DISCIPLINE_KEY = 'discipline';
const DISCIPLINE_ASSIGNMENTS_KEY = 'discipline-assignments';
const DISCIPLINE_ASSIGNMENTS_BASE = '/discipline/assignments';

function communityKey(parentKey: string, communityId: number): string {
  return `${parentKey}-${communityId}`;
}

function buildDisciplineGroup(communities: CommunityOption[]) {
  return {
    key: DISCIPLINE_KEY,
    icon: <SafetyOutlined />,
    label: 'Kỉ Luật',
    children: [
      {
        key: DISCIPLINE_ASSIGNMENTS_KEY,
        icon: <FileTextOutlined />,
        label: 'Bài tập',
        children: communities
          .filter((c) => !isHiddenCommunity(c.id))
          .map((c) => ({
            key: communityKey(DISCIPLINE_ASSIGNMENTS_KEY, c.id),
            label: (
              <Link
                to={`${DISCIPLINE_ASSIGNMENTS_BASE}?communityId=${c.id}`}
              >
                {c.name ?? `Cộng đồng #${c.id}`}
              </Link>
            ),
          })),
      },
    ],
  };
}

function buildGroupItem(group: MenuGroup, communities: CommunityOption[]) {
  return {
    key: group.parentKey,
    icon: group.icon,
    label: group.label,
    children: [
      {
        key: group.allKey,
        icon: <AppstoreOutlined />,
        label: <Link to={group.basePath}>Tất cả</Link>,
      },
      ...communities
        .filter((c) => !isHiddenCommunity(c.id))
        .map((c) => ({
          key: communityKey(group.parentKey, c.id),
          label: (
            <Link to={`${group.basePath}?communityId=${c.id}`}>
              {c.name ?? `Cộng đồng #${c.id}`}
            </Link>
          ),
        })),
    ],
  };
}

export function AppLayout() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);

  const communitiesQuery = useQuery({
    queryKey: ['admin-communities'],
    queryFn: listCommunities,
    staleTime: 5 * 60_000,
  });

  const activeGroup = MENU_GROUPS.find((g) =>
    location.pathname.startsWith(g.basePath),
  );

  const isDisciplineAssignmentsRoute = location.pathname.startsWith(
    DISCIPLINE_ASSIGNMENTS_BASE,
  );

  const selectedCommunityId = (() => {
    const raw = searchParams.get('communityId');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const selectedKey = useMemo(() => {
    if (isDisciplineAssignmentsRoute && selectedCommunityId !== null) {
      return communityKey(DISCIPLINE_ASSIGNMENTS_KEY, selectedCommunityId);
    }
    if (!activeGroup) return '';
    if (selectedCommunityId === null) return activeGroup.allKey;
    return communityKey(activeGroup.parentKey, selectedCommunityId);
  }, [activeGroup, isDisciplineAssignmentsRoute, selectedCommunityId]);

  const defaultOpenKeys = useMemo(() => {
    if (isDisciplineAssignmentsRoute) {
      return [DISCIPLINE_KEY, DISCIPLINE_ASSIGNMENTS_KEY];
    }
    return activeGroup ? [activeGroup.parentKey] : [];
  }, [activeGroup, isDisciplineAssignmentsRoute]);

  const communities = communitiesQuery.data ?? [];
  const menuItems: MenuProps['items'] = [
    ...MENU_GROUPS.map((g) => buildGroupItem(g, communities)),
    buildDisciplineGroup(communities),
  ];

  const handleLogout = () => {
    clear();
    navigate('/login', { replace: true });
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Đăng xuất',
      onClick: handleLogout,
      danger: true,
    },
  ];

  return (
    <Layout className="app-layout">
      <Sider
        breakpoint="lg"
        collapsedWidth={64}
        width={232}
        className="app-sider"
        theme="dark"
      >
        <div className="brand">
          <img src="/logo.png" alt="DH3G" className="brand-logo" />
          <Text strong style={{ color: '#fff' }}>
            DH3G ADMIN
          </Text>
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          defaultOpenKeys={defaultOpenKeys}
          items={menuItems}
        />
      </Sider>

      <Layout>
        <Header className="app-header">
          <div className="app-header-spacer" />
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <button type="button" className="user-chip" aria-label="Tài khoản">
              <Avatar size={32} icon={<UserOutlined />} />
              <span className="user-chip-email">{user?.email ?? 'admin'}</span>
            </button>
          </Dropdown>
        </Header>

        <Content className="app-content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
